import type React from "react";

import { statusColor } from "@/features/tasks/taskUtils";
import { StatusRect } from "@/pages/sessions/components";
import { Tooltip } from "@mui/material";
import Typography from "@mui/material/Typography";

import type { Phase } from "./types";
import { getActiveTasks, getPhaseStatus, getRelevantTasks } from "./utils";

const FlowSheetIcon = () => (
  <svg
    width="17"
    height="14"
    viewBox="0 0 17 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9.85885 12.8079C10.1144 12.8079 10.3309 12.719 10.5085 12.5412C10.686 12.3634 10.7748 12.1467 10.7748 11.8909C10.7748 11.6351 10.686 11.4183 10.5085 11.2406C10.3309 11.0628 10.1144 10.9739 9.85885 10.9739C9.60335 10.9739 9.38682 11.0628 9.20925 11.2406C9.03168 11.4183 8.94289 11.6351 8.94289 11.8909C8.94289 12.1467 9.03168 12.3634 9.20925 12.5412C9.38682 12.719 9.60335 12.8079 9.85885 12.8079ZM14.3932 5.56336C14.6487 5.56336 14.8652 5.47448 15.0428 5.29672C15.2204 5.11896 15.3092 4.90218 15.3092 4.6464C15.3092 4.39062 15.2204 4.17385 15.0428 3.99608C14.8652 3.81819 14.6487 3.72924 14.3932 3.72924C14.1377 3.72924 13.9212 3.81819 13.7436 3.99608C13.566 4.17385 13.4772 4.39062 13.4772 4.6464C13.4772 4.90218 13.566 5.11896 13.7436 5.29672C13.9212 5.47448 14.1377 5.56336 14.3932 5.56336ZM3.47711 4.96729H7.55358V3.77514H3.47711V4.96729ZM3.47711 8.14635H7.55358V6.9542H3.47711V8.14635ZM1.93517 11.9215C1.53412 11.9215 1.19466 11.7824 0.916796 11.5042C0.638932 11.2261 0.5 10.8862 0.5 10.4848V1.43674C0.5 1.03525 0.638932 0.69542 0.916796 0.417252C1.19466 0.139084 1.53412 0 1.93517 0H14.1489C14.5499 0 14.8894 0.139084 15.1672 0.417252C15.4451 0.69542 15.584 1.03525 15.584 1.43674H14.3932C14.3932 1.37554 14.3677 1.31951 14.3168 1.26864C14.266 1.21765 14.21 1.19215 14.1489 1.19215H1.93517C1.87404 1.19215 1.81807 1.21765 1.76726 1.26864C1.71632 1.31951 1.69085 1.37554 1.69085 1.43674V10.4848C1.69085 10.5459 1.71632 10.602 1.76726 10.6528C1.81807 10.7038 1.87404 10.7293 1.93517 10.7293H6.16426V11.9215H1.93517ZM9.85885 14C9.27362 14 8.77618 13.795 8.36653 13.3849C7.95688 12.9748 7.75205 12.4768 7.75205 11.8909C7.75205 11.4099 7.89449 10.9845 8.17936 10.6147C8.46437 10.2449 8.82573 9.99829 9.26343 9.87497V7.67247H13.7978V6.66232C13.3601 6.539 12.9988 6.29243 12.7139 5.9226C12.4289 5.55263 12.2864 5.12723 12.2864 4.6464C12.2864 4.06052 12.4912 3.56254 12.9009 3.15244C13.3105 2.74221 13.808 2.53709 14.3932 2.53709C14.9784 2.53709 15.4759 2.74221 15.8855 3.15244C16.2952 3.56254 16.5 4.06052 16.5 4.6464C16.5 5.12723 16.3575 5.55263 16.0725 5.9226C15.7876 6.29243 15.4263 6.539 14.9886 6.66232V8.86462H10.4543V9.87497C10.892 9.99829 11.2533 10.2449 11.5381 10.6147C11.8232 10.9845 11.9657 11.4099 11.9657 11.8909C11.9657 12.4768 11.7608 12.9748 11.3512 13.3849C10.9415 13.795 10.4441 14 9.85885 14Z"
      fill="#767676"
    />
  </svg>
);

export type PhaseHeadingProps = {
  phase: Phase;
};

export const PhaseHeading: React.FC<PhaseHeadingProps> = ({ phase }) => {
  const { title, order } = phase;
  const relevantTasks = getRelevantTasks(phase.tasks);
  // make sure active is a subset of relevant
  const activeTasks = getActiveTasks(relevantTasks);
  const status = getPhaseStatus(phase);
  const color = statusColor[status];

  return (
    <div className="flex h-16 shrink-0 cursor-pointer items-center gap-5 rounded-lg border border-[#A3A3A3] px-2 py-1">
      <StatusRect color={color} />

      <div className="flex grow items-center gap-1.5">
        <Typography className="[font-family:Montserrat] text-sm leading-6 font-semibold uppercase text-[#A3A3A3]">
          Phase {order}:
        </Typography>
        <Typography className="[font-family:Montserrat] text-base leading-6 font-semibold text-black">
          {title}
        </Typography>
      </div>

      <div className="flex items-center gap-1">
        <div className="flex items-center gap-1 rounded-2xl border border-[#E5E5E5] p-[2px]">
          <div className="flex h-6 w-6 items-center justify-center">
            <FlowSheetIcon />
          </div>
          <Tooltip title="Active Tasks">
            <Typography className="[font-family:Montserrat] h-6 w-6 rounded-full bg-[#D9F1EA] text-center text-xs leading-6 font-semibold text-[#00A071]">
              {activeTasks.length}
            </Typography>
          </Tooltip>
          <Tooltip title="Relevant Tasks">
            <Typography className="[font-family:Montserrat] h-6 w-6 rounded-full bg-[#E5E5E5] text-center text-xs leading-6 font-semibold">
              {relevantTasks.length}
            </Typography>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};
